import { useEffect, useRef, useState } from 'react';
import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  type Firestore,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  UNREAD_COUNT_WINDOW,
  countUnreadMessages,
  parseThreadUnreadRequestKey,
  threadUnreadRequestKey,
  type ThreadUnreadCount,
  type ThreadUnreadRequest,
} from './threadUnreadCount';
import {
  MESSAGE_INBOX_STATE_CONFLICT_MESSAGE,
  MessageInboxSourceGoneError,
  MessageInboxStateConflictError,
  assertExpectedClassNoteContent,
  assertExpectedInboxVersion,
  classNoteContentVersion,
  isClassNoteFiledThrough,
  isClassNoteReadThrough,
  isFiledThrough,
  isReadThrough,
  messageInboxSourceVersionMs,
  messageInboxStateId,
  messageInboxStateKey,
  threadAnsweredByViewer,
  threadInboundActivityMs,
  type MessageInboxStateDoc,
  type MessageInboxStateKind,
  type MessageInboxStateWrite,
} from './messageInboxStateCore';

export {
  MESSAGE_INBOX_STATE_CONFLICT_MESSAGE,
  MessageInboxSourceGoneError,
  MessageInboxStateConflictError,
  classNoteContentVersion,
  isClassNoteFiledThrough,
  isClassNoteReadThrough,
  isFiledThrough,
  isReadThrough,
  messageInboxSourceVersionMs,
  messageInboxStateId,
  messageInboxStateKey,
  threadAnsweredByViewer,
  threadInboundActivityMs,
};
export type {
  MessageInboxStateDoc,
  MessageInboxStateKind,
  MessageInboxStateWrite,
};

export interface UseMessageInboxStatesResult {
  states: Map<string, MessageInboxStateDoc>;
  loading: boolean;
  error: string | null;
}

function requireFirestore(firestore: Firestore | null = db): Firestore {
  if (!firestore) throw new Error('Firestore not initialized.');
  return firestore;
}

function stateRef(firestore: Firestore, uid: string, kind: MessageInboxStateKind, itemId: string) {
  return doc(firestore, 'users', uid, 'messageInboxState', messageInboxStateId(kind, itemId));
}

function sourceRef(firestore: Firestore, item: MessageInboxStateWrite) {
  const collectionName = item.kind === 'thread'
    ? 'threads'
    : item.kind === 'classPost'
      ? 'classPosts'
      : item.kind === 'board'
        ? 'discussions'
        : item.kind === 'bulletin'
          ? 'bulletins'
          : item.kind === 'sub'
            ? 'subRequests'
            : 'offerings';
  return doc(firestore, collectionName, item.itemId);
}

function normalizedVersion(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : null;
}

export function useMessageInboxStates(uid: string | undefined): UseMessageInboxStatesResult {
  const [states, setStates] = useState<Map<string, MessageInboxStateDoc>>(new Map());
  const [loading, setLoading] = useState(Boolean(uid));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !uid) {
      setStates(new Map());
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, 'users', uid, 'messageInboxState'),
      (snapshot) => {
        const next = new Map<string, MessageInboxStateDoc>();
        for (const stateDoc of snapshot.docs) {
          const value = stateDoc.data() as Partial<MessageInboxStateDoc>;
          /* 'sub' BELONGS IN THIS ALLOWLIST. Leaving it out meant every sub
             filing this app wrote was thrown away the instant it was read back,
             so SubRequestRow's own filedPast could never be true, the button
             never flipped to "Put it back", and the card never moved. Codex's
             review of PR #1003 found it; sourceRef above already knew about
             'sub', which is what made the omission invisible. */
          if ((value.kind === 'thread' || value.kind === 'classPost' || value.kind === 'classNote'
              || value.kind === 'board' || value.kind === 'bulletin' || value.kind === 'sub')
              && typeof value.itemId === 'string'
              && value.itemId.length > 0) {
            next.set(messageInboxStateKey(value.kind, value.itemId), {
              id: stateDoc.id,
              kind: value.kind,
              itemId: value.itemId,
              filedThroughMs: normalizedVersion(value.filedThroughMs),
              readThroughMs: normalizedVersion(value.readThroughMs),
              filedThroughContent: typeof value.filedThroughContent === 'string'
                ? value.filedThroughContent
                : null,
              readThroughContent: typeof value.readThroughContent === 'string'
                ? value.readThroughContent
                : null,
              pinned: value.kind === 'thread' && value.pinned === true,
              updatedAt: value.updatedAt,
            });
          }
        }
        setStates(next);
        setLoading(false);
        setError(null);
      },
      (snapshotError) => {
        setStates(new Map());
        setLoading(false);
        setError(snapshotError.message);
      }
    );
    return unsubscribe;
  }, [uid]);

  return { states, loading, error };
}

/**
 * Counts already proven for this session, keyed by viewer + the viewer's own
 * alias signature + thread + both cursors (see threadUnreadRequestKey).
 * Every input that could change the answer is in the key, so an entry is a
 * fact rather than a cache that can go stale: one new message moves
 * inboundMs, one read moves readThroughMs, a recall of the newest message
 * moves previewMark (B48), and each mints a different key.
 * The alias signature is in the key for the SAME reason (J32 re-pass finding
 * 3): a count fetched before the directory loaded (an empty alias set,
 * literal-uid-only exclusion) is a DIFFERENT fact from the same request
 * fetched once her alias set is known, and must not be served as the same
 * cache entry once the real set arrives. Cleared wholesale rather than
 * evicted one by one, because the only thing being defended against is
 * unbounded growth across a long session, not memory pressure.
 */
const threadUnreadCountCache = new Map<string, ThreadUnreadCount>();
const THREAD_UNREAD_CACHE_LIMIT = 300;

/**
 * The count of new messages behind each unread conversation, for the badge
 * on its inbox row (Bobbie, 2026-08-13: "add a badge count in the message of
 * the number of new messages on any thread").
 *
 * Callers pass ONLY the threads their own unread test already flagged, so
 * this never walks the whole inbox, and each fetch asks for messages newer
 * than that viewer's read cursor with a hard UNREAD_COUNT_WINDOW limit. The
 * cost of one unread conversation is therefore bounded no matter how long
 * the conversation is. A thread whose count has not arrived yet, or whose
 * fetch failed, is simply absent from the returned map: the row keeps its
 * plain unread dot instead of printing a number nobody proved.
 *
 * Rules note: this reads threads/{id}/messages, which participants may
 * already list (the full conversation page runs the same read), and it needs
 * no composite index because createdAt is indexed on its own.
 *
 * `viewerAliasUids` (J32 re-pass finding 4, P3): the caller's own alias set
 * (Messages.tsx already holds one, aliasSetsByUid), passed straight through
 * to countUnreadMessages so her OWN other sign-in's send never counts as
 * unread mail for her. Omitted, behaviour is unchanged (literal viewerUid
 * only).
 */
export function useThreadUnreadCounts(
  requests: readonly ThreadUnreadRequest[],
  viewerUid: string | undefined,
  viewerAliasUids?: ReadonlySet<string> | readonly string[]
): Map<string, ThreadUnreadCount> {
  const [counts, setCounts] = useState<Map<string, ThreadUnreadCount>>(new Map());
  const signature = requests.map(threadUnreadRequestKey).sort().join('\n');
  // Read through a ref (the same pattern threads.ts's useFindOrCreateDm
  // uses for its own directory), never a raw effect dependency: Messages.tsx
  // builds this set fresh every render (aliasSetsByUid.get(...) ?? [...]),
  // and depending on that array's IDENTITY would re-fetch every render
  // instead of only when the SET's CONTENT actually changes.
  const aliasUidsRef = useRef(viewerAliasUids);
  aliasUidsRef.current = viewerAliasUids;
  // A content signature drives the effect's own dependency instead - stable
  // across renders that pass an equal-but-differently-referenced set.
  const aliasSignature = viewerAliasUids ? [...new Set(viewerAliasUids)].sort().join(',') : '';

  useEffect(() => {
    const keys = signature ? signature.split('\n') : [];
    if (!db || !viewerUid || keys.length === 0) {
      setCounts((current) => (current.size === 0 ? current : new Map()));
      return;
    }
    const firestore = db;
    let live = true;
    void (async () => {
      const resolved = await Promise.all(keys.map(async (key) => {
        const request = parseThreadUnreadRequestKey(key);
        if (!request) return null;
        // J32 re-pass finding 3 (P3): aliasSignature must be part of the key.
        // Without it, a count computed BEFORE the directory loaded (an empty
        // viewerAliasUids, literal-uid-only exclusion) is cached under the
        // same key a later render - now holding the real alias set - would
        // reuse, serving a stale, too-high count forever for that read
        // cursor. Two counts for the SAME request that differ only by which
        // alias set computed them are not the same fact.
        const cacheKey = `${viewerUid}|${aliasSignature}|${key}`;
        const cached = threadUnreadCountCache.get(cacheKey);
        if (cached) return [request.threadId, cached] as const;
        try {
          const snapshot = await getDocs(query(
            collection(firestore, 'threads', request.threadId, 'messages'),
            where('createdAt', '>', Timestamp.fromMillis(request.readThroughMs)),
            orderBy('createdAt', 'desc'),
            limit(UNREAD_COUNT_WINDOW)
          ));
          const count = countUnreadMessages(
            snapshot.docs.map((message) => message.data() as {
              authorUid?: string | null;
              recalled?: boolean;
            }),
            viewerUid,
            UNREAD_COUNT_WINDOW,
            aliasUidsRef.current
          );
          if (threadUnreadCountCache.size >= THREAD_UNREAD_CACHE_LIMIT) {
            threadUnreadCountCache.clear();
          }
          threadUnreadCountCache.set(cacheKey, count);
          return [request.threadId, count] as const;
        } catch {
          // A number nobody could prove is worse than no badge at all.
          return null;
        }
      }));
      if (!live) return;
      const next = new Map<string, ThreadUnreadCount>();
      for (const entry of resolved) {
        if (entry) next.set(entry[0], entry[1]);
      }
      setCounts(next);
    })();
    return () => {
      live = false;
    };
  }, [signature, viewerUid, aliasSignature]);

  return counts;
}

export function inboxStateFor(
  states: Map<string, MessageInboxStateDoc>,
  kind: MessageInboxStateKind,
  itemId: string
): MessageInboxStateDoc | undefined {
  return states.get(messageInboxStateKey(kind, itemId));
}

export async function currentMessageInboxStateWrite(
  uid: string,
  kind: MessageInboxStateKind,
  itemId: string,
  firestore: Firestore | null = db
): Promise<MessageInboxStateWrite> {
  const activeDb = requireFirestore(firestore);
  const item: MessageInboxStateWrite = { kind, itemId, expectedVersionMs: 0 };
  const snapshot = await getDoc(sourceRef(activeDb, item));
  if (!snapshot.exists()) throw new MessageInboxSourceGoneError();
  if (kind === 'classNote') {
    return { ...item, expectedContent: classNoteContentVersion(snapshot.data()) };
  }
  return {
    ...item,
    expectedVersionMs: messageInboxSourceVersionMs(kind, snapshot.data(), uid),
  };
}

async function writeMessageInboxItems(
  uid: string,
  items: MessageInboxStateWrite[],
  mode: 'file' | 'read',
  firestore: Firestore | null = db
): Promise<void> {
  const activeDb = requireFirestore(firestore);
  if (!uid) throw new Error('Sign in required.');
  const unique = [...new Map(
    items.map((item) => [messageInboxStateKey(item.kind, item.itemId), item])
  ).values()];
  if (unique.length === 0) return;

  await runTransaction(activeDb, async (tx) => {
    const sourceSnapshots = await Promise.all(
      unique.map((item) => tx.get(sourceRef(activeDb, item)))
    );
    const stateSnapshots = await Promise.all(
      unique.map((item) => tx.get(stateRef(activeDb, uid, item.kind, item.itemId)))
    );

    unique.forEach((item, index) => {
      const sourceSnapshot = sourceSnapshots[index];
      if (!sourceSnapshot.exists()) throw new MessageInboxSourceGoneError();
      const currentVersionMs = messageInboxSourceVersionMs(item.kind, sourceSnapshot.data(), uid);
      const currentContent = item.kind === 'classNote'
        ? classNoteContentVersion(sourceSnapshot.data())
        : null;
      if (item.kind === 'classNote') {
        assertExpectedClassNoteContent(currentContent ?? '', item.expectedContent);
      } else {
        assertExpectedInboxVersion(currentVersionMs, item.expectedVersionMs);
      }
      const previous = stateSnapshots[index].exists()
        ? stateSnapshots[index].data() as Partial<MessageInboxStateDoc>
        : {};
      const previousFiled = normalizedVersion(previous.filedThroughMs);
      const previousRead = normalizedVersion(previous.readThroughMs);
      const previousFiledContent = typeof previous.filedThroughContent === 'string'
        ? previous.filedThroughContent
        : null;
      const previousReadContent = typeof previous.readThroughContent === 'string'
        ? previous.readThroughContent
        : null;
      const previousPinned = item.kind === 'thread' && previous.pinned === true;
      tx.set(stateRef(activeDb, uid, item.kind, item.itemId), {
        kind: item.kind,
        itemId: item.itemId,
        filedThroughMs: item.kind === 'classNote'
          ? previousFiled
          : mode === 'file'
            ? currentVersionMs
            : previousFiled,
        readThroughMs: item.kind === 'classNote'
          ? previousRead
          : Math.max(previousRead ?? 0, currentVersionMs),
        filedThroughContent: item.kind === 'classNote'
          ? mode === 'file'
            ? currentContent
            : previousFiledContent
          : null,
        readThroughContent: item.kind === 'classNote'
          ? currentContent
          : previousReadContent,
        pinned: previousPinned,
        updatedAt: serverTimestamp(),
      });
    });
  });
}

export async function fileMessageInboxItems(
  uid: string,
  items: MessageInboxStateWrite[],
  firestore: Firestore | null = db
): Promise<void> {
  return writeMessageInboxItems(uid, items, 'file', firestore);
}

/** Every inbox kind that is versioned by a moment rather than by its text. */
export type MessageInboxCursorKind = Exclude<MessageInboxStateKind, 'classNote'>;

export type MessageInboxCursorOutcome =
  /** Filed through the moment asked for. Nothing newer was behind it. */
  | 'filed'
  /** The read cursor moved to the moment asked for. The filing was untouched. */
  | 'read'
  /** File only: the source has moved past that moment, so it did not file. */
  | 'moved-on';

/**
 * MOVE ONE CURSOR THROUGH AN EXACT MOMENT, never through whatever the source
 * happens to say at tap time.
 *
 * The pair above (currentMessageInboxStateWrite then file/markRead) means "I am
 * looking at the whole conversation right now, file all of it". That is the
 * right reading for the conversation row and for opening the conversation
 * itself, and the wrong one for a notification card: a card was minted for ONE
 * message, and a reply that landed after it is something she has never seen.
 * Advancing to the source's current version there consumes that reply silently,
 * which is what commit b51b6771 ("close inbox and calendar races") removed from
 * the bell in the first place.
 *
 * So this takes the moment as an argument and clamps it: never past what the
 * source actually holds (so a card stamped a beat after its message still
 * lands on the message), and never backwards over a cursor that is already
 * further along (Math.max, same as every other writer here).
 *
 * READ mode always writes, even when the source has moved on: the card's own
 * message really was seen, and the newer one stays unread because the cursor
 * stops short of it. FILE mode refuses in that case and answers 'moved-on', so
 * a conversation with something new in it is never filed away to Past behind
 * her back; the caller says so out loud.
 *
 * READS PER CALL: two, both inside the transaction (the source doc and this
 * viewer's cursor doc), plus one write. It needs no getDoc of its own
 * beforehand, because the expected moment comes from the card rather than from
 * the source, so it costs one read LESS than the
 * currentMessageInboxStateWrite + fileMessageInboxItems pair it replaces.
 */
export async function writeMessageInboxCursorThrough(
  uid: string,
  kind: MessageInboxCursorKind,
  itemId: string,
  throughMs: number,
  mode: 'read' | 'file',
  firestore: Firestore | null = db
): Promise<MessageInboxCursorOutcome> {
  const activeDb = requireFirestore(firestore);
  if (!uid) throw new Error('Sign in required.');
  const item: MessageInboxStateWrite = { kind, itemId, expectedVersionMs: 0 };
  return runTransaction(activeDb, async (tx) => {
    const sourceSnapshot = await tx.get(sourceRef(activeDb, item));
    if (!sourceSnapshot.exists()) throw new MessageInboxSourceGoneError();
    const stateSnapshot = await tx.get(stateRef(activeDb, uid, kind, itemId));
    const currentVersionMs = messageInboxSourceVersionMs(kind, sourceSnapshot.data(), uid);
    const requested = Number.isFinite(throughMs) ? Math.floor(throughMs) : 0;
    const cursorMs = Math.max(0, Math.min(requested, currentVersionMs));
    const movedOn = currentVersionMs > cursorMs;
    const previous = stateSnapshot.exists()
      ? stateSnapshot.data() as Partial<MessageInboxStateDoc>
      : {};
    const previousFiled = normalizedVersion(previous.filedThroughMs);
    const previousRead = normalizedVersion(previous.readThroughMs);
    const nextFiled = mode === 'file' && !movedOn
      ? Math.max(previousFiled ?? 0, cursorMs)
      : previousFiled;
    tx.set(stateRef(activeDb, uid, kind, itemId), {
      kind,
      itemId,
      filedThroughMs: nextFiled,
      readThroughMs: Math.max(previousRead ?? 0, cursorMs),
      filedThroughContent: null,
      readThroughContent: typeof previous.readThroughContent === 'string'
        ? previous.readThroughContent
        : null,
      pinned: kind === 'thread' && previous.pinned === true,
      updatedAt: serverTimestamp(),
    });
    if (mode === 'read') return 'read';
    return movedOn ? 'moved-on' : 'filed';
  });
}

export async function markMessageInboxItemsRead(
  uid: string,
  items: MessageInboxStateWrite[],
  firestore: Firestore | null = db
): Promise<void> {
  return writeMessageInboxItems(uid, items, 'read', firestore);
}

export async function unfileMessageInboxItems(
  uid: string,
  items: Array<Pick<MessageInboxStateWrite, 'kind' | 'itemId'>>,
  firestore: Firestore | null = db
): Promise<void> {
  const activeDb = requireFirestore(firestore);
  if (!uid) throw new Error('Sign in required.');
  if (items.length === 0) return;
  await runTransaction(activeDb, async (tx) => {
    const unique = [...new Map(
      items.map((item) => [messageInboxStateKey(item.kind, item.itemId), item])
    ).values()];
    const snapshots = await Promise.all(
      unique.map((item) => tx.get(stateRef(activeDb, uid, item.kind, item.itemId)))
    );
    unique.forEach((item, index) => {
      const snapshot = snapshots[index];
      if (!snapshot.exists()) return;
      const previous = snapshot.data() as Partial<MessageInboxStateDoc>;
      tx.set(stateRef(activeDb, uid, item.kind, item.itemId), {
        kind: item.kind,
        itemId: item.itemId,
        filedThroughMs: null,
        readThroughMs: normalizedVersion(previous.readThroughMs),
        filedThroughContent: null,
        readThroughContent: typeof previous.readThroughContent === 'string'
          ? previous.readThroughContent
          : null,
        pinned: item.kind === 'thread' && previous.pinned === true,
        updatedAt: serverTimestamp(),
      });
    });
  });
}

/**
 * Pin a real conversation for this person only. Pinning also returns a
 * previously filed conversation to the live inbox; unpinning never files it.
 */
export async function setMessageInboxThreadPinned(
  uid: string,
  threadId: string,
  pinned: boolean,
  firestore: Firestore | null = db
): Promise<void> {
  const activeDb = requireFirestore(firestore);
  if (!uid) throw new Error('Sign in required.');
  const item: MessageInboxStateWrite = {
    kind: 'thread',
    itemId: threadId,
    expectedVersionMs: 0,
  };
  await runTransaction(activeDb, async (tx) => {
    const threadSnapshot = await tx.get(sourceRef(activeDb, item));
    if (!threadSnapshot.exists()) throw new Error('That conversation is no longer available.');
    const ref = stateRef(activeDb, uid, 'thread', threadId);
    const snapshot = await tx.get(ref);
    const previous = snapshot.exists()
      ? snapshot.data() as Partial<MessageInboxStateDoc>
      : {};
    tx.set(ref, {
      kind: 'thread',
      itemId: threadId,
      filedThroughMs: pinned ? null : normalizedVersion(previous.filedThroughMs),
      readThroughMs: normalizedVersion(previous.readThroughMs),
      filedThroughContent: null,
      readThroughContent: null,
      pinned,
      updatedAt: serverTimestamp(),
    });
  });
}
